<template>
    <RedesignBaseForm
        ref="editWidgetForm"
        :model="model"
        :rules="rules"
        :show-message="false"
        class="widget-edit-form"
    >
        <BaseCollapse v-model="activeCollapse">
            <RedesignFormItem
                :label="$t('widget.title')"
                :rules="[required]"
                prop="Title"
            >
                <RedesignBaseInput v-model="model.Title" />
            </RedesignFormItem>
            <RedesignFormItem
                v-if="isWidgetRefreshInterval"
                :label="$t('widget.widgetRefreshInterval')"
                :info-text="$t('widget.setIntervalOnWidgetReload')"
            >
                <RedesignWidgetRefreshInterval
                    v-model="model.WidgetLayout.DefaultRefreshInterval"
                    :min-value="minIntervalValue"
                    :max-value="maxIntervalValue"
                />
            </RedesignFormItem>
            <div
                v-if="TABLE_TEMPLATE_LIST.includes(Number(model.TemplateID))"
                class="mb-5"
            >
                <BaseCollapseItem
                    :title="$t('settings.sorting')"
                    name="sorting"
                    class="simple-collapse"
                >
                    <RedesignSortingParams
                        v-model="model.WidgetLayout.sortingParams"
                        :template-columns="model.WidgetTemplateColumns"
                    />
                </BaseCollapseItem>
            </div>
            <RedesignFormItem v-if="isMultiQueuesDashboard(model)">
                <div class="flex w-full flex-col gap-3">
                    <div class="flex">
                        <RedesignCheckbox
                            v-model="model.WidgetLayout.showStatsInPercentage"
                        >
                            {{ $t('widget.showStatsInPercentage') }}
                        </RedesignCheckbox>
                    </div>
                    <div
                        v-if="canToggleDisplayMode"
                        class="flex"
                    >
                        <RedesignCheckbox
                            v-model="displayQueuesAsRow"
                        >
                            {{ $t('widget.config.displayQueuesAsRows') }}
                        </RedesignCheckbox>
                    </div>
                    <div class="flex">
                        <RedesignCheckbox
                            v-model="model.WidgetLayout.displayRowWithTotals"
                        >
                            {{ $t('widget.displayTheRowWithTotals') }}
                        </RedesignCheckbox>
                    </div>
                </div>
            </RedesignFormItem>
            <template v-if="isQueueGauge(model)">
                <RedesignFormItem
                    :label="$t('widget.config.warningRangeValue')"
                >
                    <RedesignInputNumber
                        v-model="warningLayoutRange"
                        :max-value="maximumLayoutRange - 1"
                        :min-value="0"
                        :step="1"
                    />
                </RedesignFormItem>
                <RedesignFormItem
                    :label="$t('widget.config.maximumRangeValue')"
                >
                    <RedesignInputNumber
                        v-model="maximumLayoutRange"
                        :max-value="1000"
                        :min-value="0"
                        :step="2"
                    />
                </RedesignFormItem>
                <RedesignFormItem>
                    <RedesignCheckbox
                        v-model="model.WidgetLayout.showWaitingTimeTable"
                    >
                        {{ $t('widget.showWaitingTimeTable') }}
                    </RedesignCheckbox>
                </RedesignFormItem>
            </template>
            <RedesignFormItem v-if="isFunnelChartWidget(model)">
                <RedesignCheckbox
                    v-model="model.WidgetLayout.showAutoDialerTable"
                >
                    {{ $t('widget.showAutoDialerTable') }}
                </RedesignCheckbox>
                <RedesignCheckbox
                    v-model="model.WidgetLayout.displayTableAsRow"
                >
                    {{ $t('widget.config.displayTableAsRow') }}
                </RedesignCheckbox>
                <RedesignCheckbox
                    v-model="model.WidgetLayout.rotateChartHorizontally"
                >
                    {{ $t('widget.config.rotateChartHorizontally') }}
                </RedesignCheckbox>
            </RedesignFormItem>
            <div v-if="isQueueChart(model)">
                <RedesignFormItem
                    :label="$t('queues.to.display')"
                >
                    <RedesignBaseSelect
                        v-model="model.WidgetLayout.showQueues"
                        :data="allQueues"
                        :config="{
                            labelKey: 'QueueName',
                            valueKey: 'QueueID'
                        }"
                        full-width
                    />
                </RedesignFormItem>
                <RedesignFormItem
                    :label="$t('blocks.to.display')"
                >
                    <RedesignBaseSelect
                        v-model="model.WidgetLayout.showSeries"
                        :data="allSeries"
                        :config="{
                            labelKey: 'label',
                            valueKey: 'value'
                        }"
                        full-width
                    />
                </RedesignFormItem>
                <div class="mb-5">
                    <RedesignCheckbox
                        :value="queueChartShowLogout"
                        @update:checked="queueChartToggleLogoutSerie"
                    >
                        {{ $t('settings.extension.logout') }}
                    </RedesignCheckbox>
                </div>
            </div>
            <RedesignFormItem
                v-if="isQueueDashboardWidget(model)"
                :label="$t('statistics.to.display')"
            >
                <RedesignBaseSelect
                    v-model="model.WidgetLayout.ShowStatistics"
                    :data="queueDashboardStatisticOptions"
                    :config="{
                        labelKey: 'key',
                        valueKey: 'key'
                    }"
                    full-width
                />
                <div class="flex w-full flex-col gap-3 py-3">
                    <div class="flex">
                        <RedesignCheckbox v-model="model.WidgetLayout.SumOfOthers">
                            {{ $t('widget.displayPercentOfOthersValue') }}
                        </RedesignCheckbox>
                    </div>
                    <div class="flex">
                        <RedesignCheckbox v-model="model.WidgetLayout.AbsoluteNumbers">
                            {{ $t('widget.displayAbsoluteNumbers') }}
                        </RedesignCheckbox>
                    </div>
                    <div class="flex">
                        <RedesignCheckbox v-model="model.WidgetLayout.stretchToWidgetSize">
                            {{ $t('widget.stretchToWidgetSize') }}
                        </RedesignCheckbox>
                    </div>
                </div>
                <div class="mb-3">
                    <div class="text-sm mb-1">
                        {{ $t('widget.innerAlignment') }}
                    </div>
                    <RedesignRadioGroup v-model="cardTextAlign">
                        <RedesignRadioButton
                            v-for="option in TEXT_ALIGN_OPTIONS"
                            :key="option.value"
                            :label="option.value"
                            :name="option.value"
                        >
                            <RedesignBaseIcon
                                :name="option.icon"
                                :size="20"
                            />
                        </RedesignRadioButton>
                    </RedesignRadioGroup>
                </div>
                <QueueDashboardReorderModal
                    :visible="reorderQueueDashboard"
                    :model-value="queueDashboardConfig"
                    :active-keys="model.WidgetLayout.ShowStatistics || []"
                    @close="reorderQueueDashboard = false"
                    @update:modelValue="updateQueueDashboardConfig"
                    @cancel="cancelQueueDashBoardReorder"
                />
                <RedesignBaseButton
                    size="small"
                    icon="ArrowUpDown"
                    @click="openReorderQueueDashboard"
                >
                    {{ $t('common.reorder') }}
                </RedesignBaseButton>
            </RedesignFormItem>
            <div
                v-if="isQueueActivityGauge(model)"
                class="mb-5"
            >
                <BaseCollapseItem
                    :title="$t('widget.config')"
                    name="queueActivity"
                    class="simple-collapse"
                >
                    <RedesignActivityGaugeConfig
                        v-model="model.WidgetLayout"
                    />
                </BaseCollapseItem>
            </div>
            <div class="mb-5">
                <BaseCollapseItem
                    :title="$t('widget.layout')"
                    class="simple-collapse !pb-0"
                    name="layout"
                >
                    <RedesignWidgetLayoutConfig
                        v-model="model.WidgetLayout"
                        :available-colors="availableColors"
                        :is-html-editor="isHtmlEditor(model)"
                        :is-note-list-widget="isNoteListWidget(model)"
                        :is-queue-dashboard-widget="isQueueDashboardWidget(model)"
                        :is-clock-and-date-widget="isClockAndDateWidget(model)"
                        :is-queue-gauge="isQueueGauge(model)"
                        :is-pie-widget="isPieWidget(model)"
                        :is-table-widget="isTableWidget(model)"
                        :is-funnel-chart-widget="isFunnelChartWidget(model)"
                        :is-chart-widget="isChartWidget(model)"
                        :is-extension-card-statistics-widget="isExtensionCardStatisticsWidget(model)"
                    />
                </BaseCollapseItem>
            </div>
            <div
                v-if="isMultiQueuesDashboard(model)"
                class="mb-5"
            >
                <BaseCollapseItem
                    :title="$t('settings.sounds')"
                    name="sounds"
                    class="simple-collapse"
                >
                    <WidgetSoundsSettings
                        v-model="model.WidgetLayout"
                    />
                </BaseCollapseItem>
            </div>
            <RedesignFormItem v-if="showTimeFrame">
                <div v-if="model.WidgetTime?.type">
                    <BaseCollapseItem
                        :title="$t('settings.time.frame')"
                        name="widget-time-frame"
                        class="simple-collapse"
                    >
                        <RedesignWidgetTimeFrame
                            v-model="model.WidgetTime"
                            :time-frame-type="model.WidgetTime?.type"
                            :widget-time-options="widgetTimeOptions"
                        >
                            <template #frame-types>
                                <RedesignRadioGroup
                                    v-model="model.WidgetTime.type"
                                    class="radio-groups mb-4"
                                >
                                    <RedesignRadioButton
                                        v-for="type in createWidgetTimeTypes"
                                        :key="type.value"
                                        :label="type.value"
                                    >
                                        {{ type.label }}
                                    </RedesignRadioButton>
                                </RedesignRadioGroup>
                            </template>
                        </RedesignWidgetTimeFrame>
                    </BaseCollapseItem>
                </div>
            </RedesignFormItem>
            <div
                v-if="isRealtimeWidget(model) && widgetSettings"
                class="mb-5"
            >
                <BaseCollapseItem
                    :title="$t('settings.threshold')"
                    name="threshold"
                    class="simple-collapse"
                >
                    <WidgetRealTimeSettings
                        :value="widgetSettings"
                        :data-type="model.DataTypeID"
                        class="mb-5"
                        @input="updateWidgetSettings"
                    />
                </BaseCollapseItem>
            </div>
            <div
                v-if="widgetConfig.filters.length"
                class="mb-5"
            >
                <BaseCollapseItem
                    :title="$t('settings.filters')"
                    name="filters"
                    class="simple-collapse"
                >
                    <div
                        v-if="autoCompletes.length"
                        class="flex flex-col text-main-sm items-start main-text-color gap-2 mb-5"
                    >
                        {{ $t('tooltip.refresh.entities.list') }}
                        <RefreshButton
                            :disabled="loadEntitiesList"
                            :loading="loadEntitiesList"
                            @click="refreshEntitiesList"
                        />
                    </div>
                    <div
                        v-for="(filter, index) in widgetConfig.filters"
                        :key="index"
                    >
                        <RedesignWidgetConfigEntityTree
                            v-if="isAutoComplete(filter)"
                            :key="`filter-tree-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            :ordered-list="model.WidgetLayout?.sortingParams?.defaultList"
                            :show-order-component="isTableWidget(model)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                            @update:sortingParams="handleSortingParamsUpdate"
                        />
                        <RedesignWidgetConfigAutoComplete
                            v-if="isAutoCompleteSelect(filter)"
                            :key="`filter-auto-complete-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                        />
                        <RedesignWidgetConfigSelectExtended
                            v-if="isSelectExtended(filter)"
                            :key="`filter-select-extended-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                        />
                        <RedesignWidgetConfigSelect
                            v-if="isMultiSelect(filter)"
                            :key="`filter-select-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            @update:value="updateFilterItem"
                        />
                        <RedesignCheckbox
                            v-if="isAutoComplete(filter) && (Number(model.TemplateID) === 75)"
                            v-model="model.WidgetLayout.hideLoggedOff"
                        >
                            <span class="main-text-color">
                                {{ $t('widget.hideLoggedOff') }}
                            </span>
                        </RedesignCheckbox>
                    </div>
                </BaseCollapseItem>
            </div>
            <div
                v-if="widgetConfig.otherFilters.length"
            >
                <BaseCollapseItem
                    :title="$t('settings.other.filters')"
                    name="otherFilters"
                    class="simple-collapse"
                >
                    <div
                        v-for="(filter, index) in widgetConfig.otherFilters"
                        :key="`other-filter-${index}`"
                        class="mb-4"
                    >
                        <RedesignWidgetOtherFilters
                            :value="getWidgetConfig(filter)"
                            :parameter-type="filter.ParameterType"
                            @update:value="updateFilterItem"
                        />
                    </div>
                </BaseCollapseItem>
            </div>
            <div>
                <RedesignFormItem v-if="isQueueTable(model)">
                    <RedesignCheckbox v-model="hideVirtualHoldCalls">
                        {{ i18n.t('widget.settings.queue.activeCalls.hideVirtual') }}
                    </RedesignCheckbox>
                </RedesignFormItem>
            </div>
            <RedesignFormItem v-if="isClockAndDateWidget(model)">
                <div class="flex w-full flex-col gap-3">
                    <div class="flex">
                        <RedesignCheckbox v-model="model.WidgetLayout.show_date">
                            {{ $t('widget.showDate') }}
                        </RedesignCheckbox>
                    </div>
                    <div class="flex">
                        <RedesignCheckbox v-model="model.WidgetLayout.show_time">
                            {{ $t('widget.showTime') }}
                        </RedesignCheckbox>
                    </div>
                    <RedesignFormItem
                        v-if="model.WidgetLayout.show_time"
                        :label="$t('widget.clock.time.format')"
                    >
                        <RedesignCustomSelect
                            :value="model.WidgetLayout?.timeFormat"
                            :options="timeFormatOptions"
                            :default-value="DEFAULT_TIME_FORMAT"
                            :custom-input-label="String($t('widget.clock.time.format.customInput'))"
                            :custom-input-placeholder="String($t('widget.clock.time.format.customPlaceholder'))"
                            @update:value="handleTimeFormatUpdate"
                        />
                    </RedesignFormItem>
                    <RedesignFormItem
                        v-if="model.WidgetLayout.show_date"
                        :label="$t('widget.clock.date.format')"
                    >
                        <RedesignCustomSelect
                            :value="model.WidgetLayout?.dateFormat"
                            :options="dateFormatOptions"
                            :default-value="DEFAULT_DATE_FORMAT"
                            :custom-input-label="String($t('widget.clock.date.format.customInput'))"
                            :custom-input-placeholder="String($t('widget.clock.date.format.customPlaceholder'))"
                            @update:value="handleDateFormatUpdate"
                        />
                    </RedesignFormItem>
                </div>
            </RedesignFormItem>
            <div
                v-if="wizardConfigs.length"
                class="mt-5"
            >
                <WidgetParameterSetupCard
                    ref="widgetParameterSetupCardRef"
                    :configs="wizardConfigs"
                    update-mode
                    @update:config="updateFilterItem"
                />
            </div>
        </BaseCollapse>
    </RedesignBaseForm>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import { widgetModelData } from '@/composables/useWidgetEditModal'
import {
    getWidgetParameterConfig,
    isChartWidget,
    isClockAndDateWidget,
    isExtensionCardStatisticsWidget,
    isFunnelChartWidget,
    isHtmlEditor,
    isMultiQueuesDashboard,
    isNoteListWidget,
    isPieWidget,
    isQueueActivityGauge,
    isQueueChart,
    isQueueDashboardWidget,
    isQueueGauge,
    isQueueTable,
    isRealtimeWidget,
    isTableWidget,
    shouldShowRefreshInterval,
    shouldShowTimeFrame
} from '@/helpers/widgetUtils'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignBaseForm from '@/components/RedesignBaseComponents/Form/RedesignBaseForm.vue'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import { realTimeWidgetRules } from '@/enum/widgetUpdateRules'
import useValidationRules from '@/composables/useValidationRules'
import RedesignWidgetRefreshInterval
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetRefreshInterval.vue'
import get from 'lodash/get'
import RedesignSortingParams from '@/components/RedesignComponents/Widgets/Common/RedesignSortingParams.vue'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import store from '@/store/store'
import { getAllSeries } from '@/enum/queueConfigs'
import { Statuses } from '@/enum/redesignStatusTypes'
import RedesignActivityGaugeConfig from '@/components/RedesignComponents/Widgets/Common/RedesignActivityGaugeConfig.vue'
import RedesignWidgetLayoutConfig from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetLayoutConfig.vue'
import { widgetTimeOptions, widgetTimeTypes } from '@/enum/widgetTimeOptions'
import RedesignWidgetTimeFrame from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetTimeFrame.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadioButton from '@/components/RedesignBaseComponents/Inputs/RedesignRadioButton.vue'
import WidgetRealTimeSettings from '@/components/RedesignComponents/Widgets/Common/WidgetRealTimeSettings.vue'
import ENUM from '@/enum/parameters'
import { QueueDashboardConfig, Settings, WidgetConfig, WidgetExtended } from '@/types/dashboard'
import RefreshButton from '@/components/RefreshButton.vue'
import BaseCollapse from '@/components/RedesignBaseComponents/Collapse/BaseCollapse.vue'
import BaseCollapseItem from '@/components/RedesignBaseComponents/Collapse/BaseCollapseItem.vue'
import RedesignWidgetConfigSelect from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import RedesignWidgetOtherFilters from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetOtherFilters.vue'
import WidgetSoundsSettings from '@/components/RedesignComponents/Widgets/Common/WidgetSoundsSettings.vue'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import cloneDeep from 'lodash/cloneDeep'
import { getWidgetTemplate } from '@/composables/globalData/useTemplatesCategoryData'
import { refreshEntitiesData, selectedAccountData } from '@/composables/globalData/useEntitiesData'
import {
    getWidgetLayoutDefaultColors,
    isSelectExtended,
    isAutoComplete,
    isAutoCompleteSelect,
    withTemplateDataTypes
} from '@/components/RedesignComponents/Widgets/widget.helper'
import { DEFAULT_MAX_RANGE, DEFAULT_WARNING_RANGE } from '@/enum/gradeGauge'
import i18n from '@/i18n'
import RedesignWidgetConfigSelectExtended
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelectExtended.vue'
import { getTranslatedTimeFormats, getTranslatedDateFormats } from '@/helpers/dateTimeFormats'
import { DEFAULT_TIME_FORMAT, DEFAULT_DATE_FORMAT } from '@/enum/dateTimeFormats'
import RedesignCustomSelect from '@/components/RedesignComponents/Select/RedesignCustomSelect.vue'
import { getAllQueueStatisticArray, buildDefaultQueueDashboardConfig } from '@/enum/redesignQueueDashboardStatistics'
import {
    MULTI_QUEUE_AS_ROWS_TEMPLATE_ID,
    MULTI_QUEUE_AS_COLUMNS_TEMPLATE_ID
} from '@/enum/multiQueueTemplateIds'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import QueueDashboardReorderModal from '@/components/RedesignComponents/Modal/QueueDashboardReorderModal.vue'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'
import RedesignWidgetConfigAutoComplete
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigAutoComplete.vue'
import WidgetParameterSetupCard
    from '@/components/RedesignComponents/Widgets/Parameters/WidgetParameterSetupCard.vue'
import {
    getWizardParameters,
    isWizardParameter
} from '@/components/RedesignComponents/Widgets/widgetParameterRegistry'
const { required } = useValidationRules()

const editWidgetForm = ref<typeof RedesignBaseForm>()
const widgetParameterSetupCardRef = ref<ComponentExposed<typeof WidgetParameterSetupCard>>()

/* Props */
const props = defineProps<{
    value: WidgetExtended
}>()
/* Emits */
const emit = defineEmits<{
    (e: 'input'): void
    (e: 'update:value'): void
}>()

/* Form Model */
const model = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('update:value', val)
        emit('input', val)
    }
})
const widgetSettings = computed(() => {
    return {
        ...realTimeSettings,
        ...model.value.WidgetLayout.settings
    }
})

/* Data */
const TEXT_ALIGN_OPTIONS = [
    {
        value: 'start',
        icon: 'AlignLeft'
    },
    {
        value: 'center',
        icon: 'AlignCenter'
    },
    {
        value: 'end',
        icon: 'AlignRight'
    }
] as const

const TABLE_TEMPLATE_LIST = [ 9, 83 ]
const loadEntitiesList = ref(false)
const activeCollapse = ref([ '' ])
const reorderQueueDashboard = ref(false)

const timeFormatOptions = getTranslatedTimeFormats()
const dateFormatOptions = getTranslatedDateFormats()

/* Computed */
function updateWidgetSettings (settings: Settings) {
    model.value = {
        ...model.value,
        WidgetLayout: {
            ...model.value.WidgetLayout,
            settings
        }
    }
}

const rules = computed(() => {
    if (isRealtimeWidget(model.value)) {
        return realTimeWidgetRules(model.value.WidgetLayout)
    }
    return {}
})
const isWidgetRefreshInterval = computed(() => {
    return (widgetModelData.value
        && 'DefaultRefreshInterval' in widgetModelData.value
        && shouldShowRefreshInterval(widgetModelData.value))
})

const showTimeFrame = computed(() => {
    return widgetModelData.value && shouldShowTimeFrame(widgetModelData.value)
})

const templateSettings = computed(() => {
    return get(widgetModelData.value, 'WidgetLayout.TemplateSettings', {
        MinimumRefreshInterval: 60,
        MaximumRefreshInterval: 1800,
    })
})
const minIntervalValue = computed(() => {
    return Number(templateSettings.value.MinimumRefreshInterval)
})
const maxIntervalValue = computed(() => {
    return Number(templateSettings.value.MaximumRefreshInterval)
})
const maximumLayoutRange = computed({
    get () {
        return model.value.WidgetLayout?.maximumRange || DEFAULT_MAX_RANGE
    },
    set (val) {
        model.value.WidgetLayout = {
            ...model.value.WidgetLayout,
            maximumRange: val
        }
    }
})

const hideVirtualHoldCalls = computed({
    get () {
        return model.value.WidgetLayout?.hideVirtualHoldCalls ?? false
    },
    set (val) {
        model.value.WidgetLayout = {
            ...model.value.WidgetLayout,
            hideVirtualHoldCalls: val
        }
    }
})

const warningLayoutRange = computed({
    get () {
        return model.value.WidgetLayout?.warningRange || DEFAULT_WARNING_RANGE
    },
    set (val) {
        model.value.WidgetLayout = {
            ...model.value.WidgetLayout,
            warningRange: val
        }
    }
})
const allQueues = computed(() => {
    return store.state?.queues?.all || []
})
const allSeries = computed(() => {
    return getAllSeries(selectedAccountData.value?.AccountBreaks)
})
const queueChartShowLogout = computed(() => {
    return !!(model.value.WidgetLayout && model.value.WidgetLayout.showSeries?.length && model.value.WidgetLayout.showSeries?.includes(Statuses.LOGOUT))
})
const createWidgetTimeTypes = computed(() => {
    return widgetTimeTypes
        .map(el => {
            return {
                label: i18n.t(el.text),
                value: el.label
            }
        })
})
const autoCompletes = computed(() => {
    return model.value.WidgetConfig.filter(config => isAutoComplete(config))
})

const displayQueuesAsRow = computed({
    get: () => model.value.WidgetLayout?.displayQueuesAsRow ?? true,
    set: (value) => {
        model.value = {
            ...model.value,
            WidgetLayout: {
                ...model.value.WidgetLayout,
                displayQueuesAsRow: value
            }
        }
    }
})

const cardTextAlign = computed({
    get: () => model.value.WidgetLayout?.cardTextAlign ?? 'center',
    set: (value: 'start' | 'center' | 'end') => {
        model.value = {
            ...model.value,
            WidgetLayout: {
                ...model.value.WidgetLayout,
                cardTextAlign: value
            }
        }
    }
})

const canToggleDisplayMode = computed(() => {
    const templateId = model.value.TemplateID
    return templateId !== MULTI_QUEUE_AS_ROWS_TEMPLATE_ID &&
        templateId !== MULTI_QUEUE_AS_COLUMNS_TEMPLATE_ID
})

const queueDashboardStatisticOptions = computed(() => {
    const customConfig = model.value.WidgetLayout.queueDashboardConfig ?? {}
    return getAllQueueStatisticArray().map(statistic => {
        return {
            key: statistic.key,
            label: customConfig[statistic.key]?.customLabel ? customConfig[statistic.key].customLabel : statistic.label
        }
    })
})

const queueDashboardConfig = computed(() =>
    model.value.WidgetLayout?.queueDashboardConfig
    ?? buildDefaultQueueDashboardConfig()
)

/* Both AI parameters are configured together, in the wizard, rather than one filter row each */
const wizardConfigs = computed(() => {
    return getWizardParameters(model.value.WidgetConfig ?? []).map(getWidgetConfig)
})

const widgetConfig = computed(() => {
    if (!model.value.WidgetConfig || !model.value.WidgetConfig.length) {
        return {
            filters: [],
            otherFilters: []
        }
    }
    return model.value.WidgetConfig.reduce((acc, filter) => {
        if (isWizardParameter(filter)) {
            return acc
        }

        if (isOtherFilters(filter) && filter.ParameterType) {
            acc.otherFilters.push(filter)
        } else if (!isOtherFilters(filter)) {
            acc.filters.push(filter)
        }
        return acc
    }, {
        filters: [],
        otherFilters: []
    } as { filters: Array<WidgetConfig>, otherFilters: Array<WidgetConfig> })
})


const filtersConfig = computed(() => {
    return [
        ...ENUM.AUTO_COMPLETE_TYPE_KEYS,
        ...ENUM.MULTISELECT_TYPE_KEYS,
        ...ENUM.MULTISELECT_EXTENDED
    ]
})
const availableColors = computed(() => {
    return getWidgetLayoutDefaultColors(props.value) || {}
})

/* Methods */
function updateFilterItem (filterData: WidgetConfig) {
    const _index = model.value.WidgetConfig?.findIndex(i => i.ParameterID === filterData.ParameterID)

    if (_index > -1) {
        const widgetConfigToUpdate = cloneDeep(model.value.WidgetConfig)

        widgetConfigToUpdate[_index] = filterData

        model.value = {
            ...model.value,
            WidgetConfig: widgetConfigToUpdate
        }
    }
}

function isOtherFilters (config: WidgetConfig) {
    return !filtersConfig.value.includes(config.ParameterType || -1)
}

function updateQueueDashboardConfig (val: QueueDashboardConfig) {
    model.value = {
        ...model.value,
        WidgetLayout: {
            ...model.value.WidgetLayout,
            queueDashboardConfig: val
        }
    }
}
function cancelQueueDashBoardReorder () {
    reorderQueueDashboard.value = false
}

async function refreshEntitiesList () {
    loadEntitiesList.value = true
    await refreshEntitiesData()
    loadEntitiesList.value = false
}

function queueChartToggleLogoutSerie () {
    if (!('showSeries' in model.value.WidgetLayout)) {
        model.value.WidgetLayout.showSeries = []
    }
    const series = model.value.WidgetLayout.showSeries ? [ ...model.value.WidgetLayout.showSeries ] : []

    if (queueChartShowLogout.value) {
        const logoutSerieIndex = series?.findIndex(el => el === Statuses.LOGOUT) ?? -1
        if (logoutSerieIndex > -1) {
            series.splice(logoutSerieIndex, 1)
        }
    } else {
        series.push(Statuses.LOGOUT)
    }

    model.value.WidgetLayout.showSeries = series.length ? [ ...series ] : []
}
function getWidgetConfig (config: WidgetConfig) {
    const templateId = model.value.TemplateID
    const template = getWidgetTemplate(templateId)


    return withTemplateDataTypes({
        ...getWidgetParameterConfig(template, config.ParameterID, [ 'EntityTypeID', 'ParameterData', 'ParameterDescriptionTag', 'ParameterTitleTag', 'ParameterPrettyNameContentTag', 'ParameterPrettyName' ]),
        ...config
    }, template?.TemplateSettings?.DataType)
}

function isMultiSelect (config: WidgetConfig) {
    return ENUM.MULTISELECT_TYPE_KEYS.includes(config.ParameterType || -1)
}

function openReorderQueueDashboard () {
    reorderQueueDashboard.value = true
}

function handleTimeFormatUpdate (value: string | undefined) {
    const currentModel = { ...model.value }
    if (!currentModel.WidgetLayout) {
        currentModel.WidgetLayout = {}
    }
    currentModel.WidgetLayout = {
        ...currentModel.WidgetLayout,
        timeFormat: value
    }
    model.value = currentModel
}

function handleDateFormatUpdate (value: string | undefined) {
    const currentModel = { ...model.value }
    if (!currentModel.WidgetLayout) {
        currentModel.WidgetLayout = {}
    }
    currentModel.WidgetLayout = {
        ...currentModel.WidgetLayout,
        dateFormat: value
    }
    model.value = currentModel
}

function handleSortingParamsUpdate (params: { defaultList: (string | number)[], idKey: string }) {
    const currentModel = { ...model.value }

    if (!currentModel.WidgetLayout) {
        currentModel.WidgetLayout = {}
    }

    if (!currentModel.WidgetLayout.sortingParams) {
        currentModel.WidgetLayout.sortingParams = {
            prop: '',
            order: null
        }
    }

    currentModel.WidgetLayout.sortingParams = {
        ...currentModel.WidgetLayout.sortingParams,
        defaultList: params.defaultList,
        defaultProp: params.idKey,
        order: null
    }

    model.value = currentModel
}

async function validate () {
    const res = await editWidgetForm.value?.validate()
    let wizardParametersValid = true

    if (widgetParameterSetupCardRef.value) {
        wizardParametersValid = widgetParameterSetupCardRef.value.validate()

        if (wizardParametersValid) {
            widgetParameterSetupCardRef.value.clearError()
        } else {
            widgetParameterSetupCardRef.value.showError()
        }
    }

    return res?.isValid && wizardParametersValid
}


defineExpose({
    validate
})
</script>

<style lang="scss">
.widget-edit-form {
    .el-checkbox, .el-checkbox__input {
        white-space: normal;
    }
}
</style>
